import { ChangeEvent, FC, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { orderBy } from 'lodash';
import {
    Box,
    Divider,
    Flex,
    Heading,
    HStack,
    IconButton,
    Menu,
    MenuButton,
    MenuGroup,
    MenuItem,
    MenuList,
    Radio,
    RadioGroup,
    Select,
    Stack,
    Tag,
    Text,
    Tooltip,
    useToast,
} from '@chakra-ui/react';
import { RiAccountCircleLine } from 'react-icons/ri';
import { MdApi, MdDashboard, MdHelpOutline, MdLogout, MdOutlineChat } from 'react-icons/md';

import icons, { TIcon } from 'shared/icons';
import { useGetCompanyInfoSelector, useGetUserInfoSelector } from 'core/redux/selectors/authSelectors';
import { useGetInternationalCompaniesQuery, useUpdateUserCompanyMutation } from 'core/API/authApi';
import { IInternationalCompany } from 'types';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { useTranslation } from 'helpers/languages/translations';
import { useMultiLanguagesHandler } from 'contexts/MultiLanguagesContext';
import { backendUrl } from 'config';

const iconDefaultProps = {
    height: 'auto',
    display: 'inline',
    width: '24px',
    mr: '4',
};

const PageHeader: FC<{
    icon: { name: TIcon; props?: any };
    pageTitle: string;
    readonly?: boolean;
    children?: ReactNode;
}> = ({ icon, pageTitle, readonly, children }) => {
    const { setLocale, locale } = useMultiLanguagesHandler();
    const userInfo = useGetUserInfoSelector();
    const companyInfo = useGetCompanyInfoSelector();
    const { data, isUninitialized, isLoading } = useGetInternationalCompaniesQuery(undefined, {
        skip: userInfo && !userInfo.is_international,
    });
    const [updateUserCompany, { isLoading: isUpdateCompanyLoading }] = useUpdateUserCompanyMutation({
        fixedCacheKey: 'auth-update-user-company',
    });
    const translate = useTranslation();
    const toast = useToast();
    const navigate = useNavigate();
    const location = useLocation();

    // todo: align left the texts
    const renderAvatarMenu = () => {
        return (
            <Flex>
                <Menu>
                    <MenuButton
                        as={IconButton}
                        aria-label="Account"
                        icon={<RiAccountCircleLine size={24} color="black" />}
                        variant="ghost"
                        ml={4}
                        data-testid="profile_button"
                        borderRadius="full"
                    />

                    <MenuList zIndex={1101}>
                        <MenuGroup>
                            <MenuItem
                                _hover={{ bg: 'transparent' }}
                                _focus={{ bg: 'transparent' }}
                                _active={{ bg: 'transparent' }}
                                pb={0}
                            >
                                <Flex
                                    alignItems="center"
                                    justifyContent="space-between"
                                    pb={2}
                                    h={'100%'}
                                    w={'100%'}
                                    mx={1}
                                >
                                    <Flex
                                        alignItems="center"
                                        backgroundColor="yellow.300"
                                        justifyContent="center"
                                        borderRadius="50%"
                                        width={6}
                                        height={6}
                                        mr={2}
                                        fontWeight="semibold"
                                        fontSize="xs"
                                        py={2}
                                    >
                                        {userInfo?.username?.charAt(0).toUpperCase()}
                                    </Flex>
                                    <Text fontWeight="semibold" fontSize="sm">
                                        {userInfo?.username}
                                    </Text>
                                </Flex>
                            </MenuItem>
                        </MenuGroup>
                        <Divider borderColor="gray.200" width="auto" mx={4} />
                        <MenuGroup>
                            <MenuItem
                                px={4}
                                as="a"
                                py={2}
                                iconSpacing={2}
                                href={backendUrl('/api/zendesk/support')}
                                target="_blank"
                                icon={<MdOutlineChat size={20} />}
                            >
                                <Text fontSize="sm"> {translate('page.header.menu.zendesk')}</Text>
                            </MenuItem>

                            <Divider borderColor="gray.50" width="auto" mx={4} />

                            <MenuItem
                                px={4}
                                as="a"
                                py={2}
                                iconSpacing={2}
                                href={backendUrl('/api/zendesk/documentation')}
                                target="_blank"
                                icon={<MdHelpOutline size={20} />}
                            >
                                <Text fontSize="sm"> {translate('page.header.menu.documentation')}</Text>
                            </MenuItem>

                            <Divider borderColor="gray.50" width="auto" mx={4} />

                            <MenuItem
                                px={4}
                                as="a"
                                py={2}
                                iconSpacing={2}
                                href={backendUrl('/api/zendesk/api')}
                                target="_blank"
                                icon={<MdApi size={20} />}
                            >
                                <Text fontSize="sm"> {translate('page.header.menu.api')}</Text>
                            </MenuItem>

                            <Divider borderColor="gray.50" width="auto" mx={4} />

                            <MenuItem
                                px={4}
                                as="a"
                                py={2}
                                iconSpacing={2}
                                href={backendUrl('/api/zendesk/dashboard')}
                                target="_blank"
                                icon={<MdDashboard size={20} />}
                            >
                                <Text fontSize="sm"> {translate('page.header.menu.dashboard')}</Text>
                            </MenuItem>
                        </MenuGroup>
                        <Divider borderColor="gray.200" width="auto" mx={4} />
                        <MenuGroup>
                            <Stack>
                                <RadioGroup onChange={setLocale} value={locale} data-testid="language-selector">
                                    <Stack spacing={0}>
                                        <Box
                                            _hover={{
                                                backgroundColor: 'gray.100',
                                                cursor: 'pointer',
                                            }}
                                            px={4}
                                        >
                                            <Flex py={2}>
                                                <Radio spacing={3} w="100%" value="en" data-value="en">
                                                    <Text fontSize="sm">English</Text>
                                                </Radio>
                                            </Flex>
                                        </Box>

                                        <Divider borderColor="gray.50" width="auto" mx={4} />

                                        <Box
                                            _hover={{
                                                backgroundColor: 'gray.100',
                                                cursor: 'pointer',
                                            }}
                                            px={4}
                                            mt={0}
                                        >
                                            <Flex py={2}>
                                                <Radio spacing={3} w="100%" value="fr" data-value="fr">
                                                    <Text fontSize="sm">Français</Text>
                                                </Radio>
                                            </Flex>
                                        </Box>
                                    </Stack>
                                </RadioGroup>
                            </Stack>
                        </MenuGroup>
                        <Divider borderColor="gray.200" width="auto" mx={4} />
                        <MenuItem
                            px={4}
                            py={2}
                            as="a"
                            mr={0}
                            href="/auth/logout"
                            iconSpacing={2}
                            icon={<MdLogout size={20} />}
                        >
                            <Text fontSize="sm"> {translate('settings.logout')}</Text>
                        </MenuItem>
                    </MenuList>
                </Menu>
            </Flex>
        );
    };

    const renderInternationalCompaniesSelection = (internationalCompanies: IInternationalCompany[]) => {
        const options: any[] = [];
        const handleChange = async (e: ChangeEvent<HTMLSelectElement>) => {
            try {
                await updateUserCompany({ id: e.target.value });
                navigate(location.pathname, { replace: true });
            } catch (error) {
                toast({
                    title: (error as Error).message,
                    status: 'error',
                    isClosable: true,
                });
            }
        };

        internationalCompanies.forEach((intCompany) => {
            intCompany.companies.forEach((c) => {
                options.push(c);
            });
        });

        const orderedOptions = orderBy(options, 'country_name').map((c) => (
            <option key={c.id} value={c.id}>{`${c.country_name}_${c.id}`}</option>
        ));

        const isSU = userInfo?.email?.toLowerCase()?.includes('_su@');

        return (
            <Flex>
                <Select
                    disabled={isUpdateCompanyLoading || isSU}
                    onChange={intentionalyFloatingPromiseReturn(handleChange)}
                    value={companyInfo ? `${companyInfo.id}` : ''}
                    size="sm"
                    bg="white"
                >
                    {orderedOptions}
                </Select>
            </Flex>
        );
    };

    const Icon = icons[icon.name];

    return (
        <HStack px="32px" py="26px" justifyContent="space-between">
            <Flex alignItems="center">
                <Icon {...iconDefaultProps} {...(icon.props ? icon.props : {})} />
                <Box display="flex" alignItems="center">
                    <Heading as="h1" fontSize={'2xl'} fontWeight={'semibold'}>
                        {pageTitle}
                    </Heading>
                    {readonly && (
                        <Box minHeight="100%" display="flex" alignItems="flex-end" pt="3px">
                            <Tooltip label={translate('header.tooltip.readonly')} aria-label="Read only">
                                <Tag ml="5px" size="sm" colorScheme="gray" cursor="default">
                                    {translate('header.tag.readonly')}
                                </Tag>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
            </Flex>
            <Flex flex="1" px="24px">
                {children}
            </Flex>
            {!isUninitialized &&
                !isLoading &&
                data &&
                data.internationalCompanies &&
                data.internationalCompanies.length > 0 &&
                data.internationalCompanies[0].companies.length > 1 &&
                renderInternationalCompaniesSelection(data.internationalCompanies)}
            {renderAvatarMenu()}
        </HStack>
    );
};

export default PageHeader;
